import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { authAPI } from '../services/api';
import { clientLogger } from '../services/clientLogger';
import { sendAnalyticsEvent, setGAUserId } from '../utils/analytics';


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Performs a silent session check by calling refresh token endpoint
   */
  const checkSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const { user: refreshedUser } = await authAPI.refresh();
      setUser(refreshedUser);
      setIsAuthenticated(true);
      console.log('💚 Silent session restore successful:', refreshedUser.email);
    } catch (error) {
      console.log('ℹ️ No active session found (User is guest).');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial silent session check on mount
  useEffect(() => {
    checkSession();

    // Listen for axios interceptor's "auth-session-expired" event
    const handleSessionExpired = () => {
      console.warn('⚠️ Session expired event triggered. Logging out...');
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth-session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth-session-expired', handleSessionExpired);
    };
  }, [checkSession]);
  // Sync user context with client logger
  useEffect(() => {
    if (user) {
      clientLogger.setUser({ id: user.id, email: user.email });
    } else {
      clientLogger.setUser(null);
    }
  }, [user]);


  /**
   * User login method
   */
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const { user: loggedInUser } = await authAPI.login(email, password);
      setUser(loggedInUser);
      setIsAuthenticated(true);
      console.log('🔑 Login successful:', loggedInUser.email);
      return { success: true };
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Неверный email или пароль.';
      console.error('Login failed:', errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * User registration method
   */
  const register = async (email, password, fullName, role) => {
    setIsLoading(true);
    try {
      await authAPI.register(email, password, fullName, role);
      // Auto-login after registration is very convenient
      const { user: loggedInUser } = await authAPI.login(email, password);
      setUser(loggedInUser);
      setIsAuthenticated(true);
      console.log('🆕 Registration and login successful:', loggedInUser.email);
      return { success: true };
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Ошибка при регистрации.';
      console.error('Registration failed:', errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * User logout method
   */
  const logout = async () => {
    setIsLoading(true);
    try {
      sendAnalyticsEvent('logout');
      setGAUserId(null);
      await authAPI.logout();
    } catch (error) {
      console.error('Server logout request failed, clearing local state anyway:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      console.log('🔓 Logout successful.');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        register,
        checkSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to consume AuthContext conveniently
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
